import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCV, useUpdateCV, useProfile, useUpdateProfile } from "@/hooks/useApi";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/cn";

export function ProfileAiContextPage() {
  const { data: cv, isLoading, error } = useCV();
  const updateCv = useUpdateCV();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [memory, setMemory] = useState("");
  const [savedMemory, setSavedMemory] = useState("");
  const [memoryInitialized, setMemoryInitialized] = useState(false);
  const [memoryStatus, setMemoryStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (profile && !memoryInitialized) {
      const m = profile.assistantMemory ?? "";
      setMemory(m);
      setSavedMemory(m);
      setMemoryInitialized(true);
    }
  }, [profile, memoryInitialized]);

  // Initialize editor content when CV loads
  if (cv && !initialized) {
    setContent(cv.content ?? "");
    setSavedContent(cv.content ?? "");
    setInitialized(true);
  }

  // Reset init when CV refetches
  useMemo(() => {
    if (cv && !initialized) {
      setContent(cv.content ?? "");
      setSavedContent(cv.content ?? "");
      setInitialized(true);
    }
  }, [cv, initialized]);

  const wordCount = content
    ? content.split(/\s+/).filter(Boolean).length
    : 0;
  const charCount = content.length;
  const hasUnsavedChanges = content !== savedContent;

  async function handleSave() {
    setSaveStatus("saving");
    try {
      await updateCv.mutateAsync(content);
      setSavedContent(content);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }

  function handleDownload() {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cv.md";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleRevert() {
    setContent(savedContent);
  }

  async function handleSaveMemory() {
    setMemoryStatus("saving");
    try {
      await updateProfile.mutateAsync({ assistantMemory: memory });
      setSavedMemory(memory);
      setMemoryStatus("saved");
      setTimeout(() => setMemoryStatus("idle"), 2000);
    } catch {
      setMemoryStatus("error");
      setTimeout(() => setMemoryStatus("idle"), 3000);
    }
  }

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            <p className="text-sm text-muted-foreground">Loading CV…</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">
            Failed to load CV. Please try again later.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6 rounded-lg border border-border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
        <p>
          AI Context is the text the models read: your{" "}
          <strong className="text-foreground">Markdown CV</strong> and{" "}
          <strong className="text-foreground">assistant memory</strong>. Prefer
          editing the itemized{" "}
          <Link to="/profile/intelligence" className="text-brand hover:underline">
            Intelligence
          </Link>{" "}
          graph, then regenerate this markdown.
        </p>
        <p className="mt-2">
          Resume PDFs and other file attachments live in{" "}
          <Link to="/assets" className="text-brand hover:underline">
            Assets
          </Link>
          — those are for Apply uploads, not for Ask AI.
        </p>
      </div>

      <section className="mb-8 rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Assistant memory
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Standing instructions the AI should remember across sessions.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleSaveMemory()}
            disabled={
              updateProfile.isPending || memory === savedMemory
            }
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition",
              memoryStatus === "saved"
                ? "bg-success/20 text-success"
                : memoryStatus === "error"
                  ? "bg-destructive/20 text-destructive"
                  : "bg-brand text-brand-foreground hover:opacity-90",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {memoryStatus === "saved"
              ? "Saved!"
              : memoryStatus === "error"
                ? "Error"
                : updateProfile.isPending
                  ? "Saving…"
                  : "Save memory"}
          </button>
        </div>
        <textarea
          value={memory}
          onChange={(e) => setMemory(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="e.g. Prefer hybrid roles in Toronto; emphasize platform leadership…"
        />
      </section>

      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Markdown CV (AI-readable)
            </h2>
            <p className="text-sm text-muted-foreground">
              Paste or edit the text version Ask AI and job evaluation use.
              Manage resume PDFs in{" "}
              <Link to="/assets" className="text-brand hover:underline">
                Assets
              </Link>
              .
            </p>
          </div>
          <div className="hidden items-center gap-3 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground sm:flex">
            <span>
              <strong className="text-foreground">{wordCount}</strong> words
            </span>
            <span className="text-border">|</span>
            <span>
              <strong className="text-foreground">{charCount}</strong> chars
            </span>
          </div>
        </div>
      </header>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Markdown Editor
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={!content}
              className="rounded px-2 py-1 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-30"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!content}
              className="rounded px-2 py-1 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-30"
              title="Download as .md"
            >
              Download
            </button>
            <button
              type="button"
              onClick={handleRevert}
              disabled={!hasUnsavedChanges}
              className="rounded px-2 py-1 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-30"
            >
              Revert
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateCv.isPending || !hasUnsavedChanges}
              className={`
                inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition
                ${saveStatus === "saved"
                  ? "bg-success/20 text-success"
                  : saveStatus === "error"
                    ? "bg-destructive/20 text-destructive"
                    : "bg-brand text-brand-foreground hover:opacity-90"}
                disabled:cursor-not-allowed disabled:opacity-50
              `}
            >
              {updateCv.isPending && (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
              {saveStatus === "saved"
                ? "Saved!"
                : saveStatus === "error"
                  ? "Error saving"
                  : updateCv.isPending
                    ? "Saving…"
                    : "Save"}
            </button>
          </div>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="min-h-[500px] w-full resize-y border-0 bg-transparent p-4 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          placeholder="Paste or write your CV in Markdown…

# Your Name
## Title

### Experience
..."
          spellCheck
        />
        <div className="flex items-center justify-between border-t border-border px-4 py-2">
          <div className="flex items-center gap-3 sm:hidden">
            <span className="text-xs text-muted-foreground">
              {wordCount} words
            </span>
            <span className="text-xs text-muted-foreground">
              {charCount} chars
            </span>
          </div>
          <div />
          {hasUnsavedChanges && (
            <span className="text-xs text-warning">Unsaved changes</span>
          )}
        </div>
      </div>

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        labelledBy="cv-preview-title"
        panelClassName="max-w-3xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2
              id="cv-preview-title"
              className="text-sm font-semibold text-foreground"
            >
              Markdown CV preview
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {wordCount} words · {charCount} chars
              {hasUnsavedChanges ? " · includes unsaved edits" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPreviewOpen(false)}
            className="rounded-md px-2 py-1 text-xs text-muted-foreground transition hover:bg-muted/20 hover:text-foreground"
          >
            Close
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-4">
          {content ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-sm text-foreground">
              {content}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing to preview yet. Paste or edit your Markdown CV first.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}